<template>
	<div
		:class="{ 'tags--disabled' : disabled }"
		class="tags"
		data-cy="tags"
		aria-label="tag"
	>
		<helio-field
			ref="field"
			:label="label"
			:placeholder="inputPlaceholder"
			:icon="icon"
			:icon-weight="iconWeight"
			:large="large"
			:disabled="disabled || max"
			:append="disabled || !canAdd || items.length >= maximum ? '' : 'tag'"
			:class="{ 'tags__field--valid' : tagIsValid }"
			class="tags__field"
			@appendClick="addTag"
			@enter="addTag"
			@blurred="addTag"
			@changed="determineTagValidity"
		/>

		<ul
			class="tags__list"
			data-cy="tags-list"
		>
			<li
				v-for="(item, index) in items"
				ref="tag-item"
				:key="index"
				:class="{
					'tag__item--delete' : canDelete && !disabled,
					'tag__item--large' : large,
				}"
				:aria-label="`${$t('aria-label.tag')} : ${item}`"
				class="tag__item"
				data-cy="tags-item"
				tabindex="0"
			>
				{{ item }}

				<div
					tabindex="0"
					@keyup.enter="removeTag(item)"
					@keyup.space="removeTag(item)"
				>
					<helio-icon
						v-if="canDelete && !disabled"
						:aria-label="$t('aria-label.delete-tag')"
						class="tags__delete"
						token="times"
						weight="light"
						data-cy="tags-item-delete"
						@click.stop="removeTag(item)"
					/>
				</div>
			</li>
		</ul>
	</div>
</template>

<script>
import Field from '../field/Field.vue';
import Icon from '../icon/Icon.vue';

export default {
	name: 'Tags',

	components: {
		'helio-field': Field,
		'helio-icon': Icon,
	},

	props: {
		canAdd: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the user can add tag items to the Tag component',
		},

		canDelete: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the user can remove tag items from the Tag component',
		},

		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Tags component will be disabled for the user',
		},

		icon: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether an icon will appear in the Tags input field and which icon it will be',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the icon',
		},

		initialItems: {
			type: Array,
			required: false,
			default: () => [],
			description: 'Sets the Tag items that will be shown on load',
		},

		label: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Tag component\'s label',
		},

		large: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Tag component will display a large-format input field',
		},

		maxPlaceholder: {
			type: String,
			required: false,
			default: ' ',
			description: 'Sets the placeholder that will appear in the Tag input field when the maximum number of Tag items has been reached',
		},

		maximum: {
			type: Number,
			required: false,
			default: 999,
			validator: maximum => maximum > 0,
			description: 'Sets the maximum number of permitted Tag items',
		},

		minimumCharacters: {
			type: Number,
			required: false,
			default: 1,
			validator: minimumCharacters => minimumCharacters > 0,
			description: 'Sets the minimum number of characters a Tag item must have',
		},

		placeholder: {
			type: String,
			required: false,
			default: ' ',
			description: 'Sets the placeholder for the Tag input field',
		},
	},

	data() {
		return {
			items: null,
			max: false,
			inputPlaceholder: this.placeholder,
			tagIsValid: false,
		};
	},

	watch: {
		initialItems(items) {
			this.items = items;
		},

		items() {
			if (this.items.length === this.maximum) {
				this.max = true;
				this.inputPlaceholder = this.maxPlaceholder.replace('X', this.maximum);
			} else {
				this.max = false;
				this.inputPlaceholder = this.placeholder;
			}
		},
	},

	created() {
		this.items = this.initialItems;
	},

	methods: {
		addTag(tag) {
			const cleanedTag = tag.trim();

			if (
				!this.isDisabled
				&& this.canAdd
				&& cleanedTag !== ''
				&& cleanedTag.length >= this.minimumCharacters
				&& this.items.length < this.maximum
				&& this.items.indexOf(cleanedTag) === -1
			) {
				this.items.push(cleanedTag);
				this.$refs.field.value = '';
				this.$emit('added', this.items);
			} else if (this.items.indexOf(cleanedTag) > -1) {
				const matching = this.$refs['tag-item'][this.items.indexOf(cleanedTag)];

				matching.classList.add('tag__item--match');

				setTimeout(() => {
					matching.classList.remove('tag__item--match');
				}, 250);
			}
		},

		removeTag(tag) {
			if (!this.isDisabled && this.canDelete) {
				this.items = this.items.filter(currentTag => currentTag !== tag);
				this.$emit('removed', this.items);
			}
		},

		determineTagValidity(tag) {
			const cleanedTag = tag.trim();
			this.tagIsValid = cleanedTag.length >= this.minimumCharacters;
		},
	},
};
</script>

<style lang="scss" src="./tags.scss" />
